import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  FileText,
  FolderPlus,
  Folder as FolderIcon,
  HardDrive,
  Search,
  Upload,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useDocuments,
  useFolders,
  useSaveFolder,
  useStorageReport,
  useUploadDocument,
} from './api';
import {
  CATEGORY_LABELS,
  CONFIDENTIALITY_LABELS,
  CONFIDENTIALITY_VARIANT,
  formatBytes,
} from './labels';
import type { Folder } from './types';

export function DocumentsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder') ?? '';
  const pagination = usePagination();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [expiring, setExpiring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const debounced = useDebounce(search, 300);

  const { data: folders = [], isLoading: loadingFolders } = useFolders();
  const { data, isLoading } = useDocuments({
    page: pagination.page,
    limit: pagination.limit,
    folderId: folderId || undefined,
    q: debounced || undefined,
    category: category || undefined,
    expiringInDays: expiring ? 60 : undefined,
  });

  const current = folders.find((folder) => folder.id === folderId);

  const setFolder = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('folder', id);
    else next.delete('folder');
    setParams(next);
    pagination.reset();
  };

  return (
    <div>
      <PageHeader
        title="Documents"
        description="Everything filed, versioned, and reachable only through a link that expires."
        actions={
          <div className="flex gap-2">
            <Can permission="file.document.create">
              <Button variant="outline" onClick={() => setNewFolder(true)}>
                <FolderPlus className="h-4 w-4" />
                New folder
              </Button>
              <Button onClick={() => setUploading(true)}>
                <Upload className="h-4 w-4" />
                Upload
              </Button>
            </Can>
          </div>
        }
      />

      <StorageBanner />

      <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Folders</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {loadingFolders ? (
              <TableSkeleton rows={4} columns={1} />
            ) : (
              <FolderTree folders={folders} activeId={folderId} onSelect={setFolder} />
            )}
          </CardContent>
        </Card>

        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 pl-9"
                placeholder="Search names, tags and contents…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  pagination.reset();
                }}
              />
            </div>
            <Select
              className="h-9 w-auto min-w-[10rem]"
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                pagination.reset();
              }}
            >
              <option value="">All categories</option>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              variant={expiring ? 'secondary' : 'outline'}
              onClick={() => {
                setExpiring((value) => !value);
                pagination.reset();
              }}
            >
              Expiring soon
            </Button>
          </div>

          {current && (
            <div className="mb-3 flex items-center gap-1 text-sm text-muted-foreground">
              <button className="hover:text-foreground" onClick={() => setFolder('')}>
                All documents
              </button>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-medium text-foreground">{current.path}</span>
              {current.maxFileSizeMb && (
                <span className="ml-2 text-xs">(up to {current.maxFileSizeMb} MB here)</span>
              )}
            </div>
          )}

          {isLoading ? (
            <TableSkeleton columns={4} />
          ) : (data?.data ?? []).length === 0 ? (
            <Card>
              <CardContent className="py-14 text-center">
                <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Nothing here yet. Uploading a file against a client or project files it
                  automatically.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {(data?.data ?? []).map((document) => (
                <Card
                  key={document.id}
                  className="cursor-pointer transition-shadow hover:shadow-brand"
                  onClick={() => navigate(`/documents/${document.id}`)}
                >
                  <CardContent className="flex flex-wrap items-center gap-4 p-4">
                    <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{document.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {CATEGORY_LABELS[document.category]}
                        {document.folder && ` · ${document.folder.path}`}
                        {document.currentVersion &&
                          ` · v${document.currentVersion.version} · ${document.currentVersion.sizeLabel}`}
                      </div>
                    </div>

                    {document.expiryDate && (
                      <span
                        className={cn(
                          'shrink-0 text-xs',
                          new Date(document.expiryDate) < new Date()
                            ? 'font-medium text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        expires {formatDate(document.expiryDate)}
                      </span>
                    )}

                    {document.signatureRequestCount > 0 && (
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        {document.signatureRequestCount} signature
                        {document.signatureRequestCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {document.shareCount > 0 && (
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        shared
                      </Badge>
                    )}

                    <Badge
                      variant={CONFIDENTIALITY_VARIANT[document.confidentiality]}
                      className="shrink-0"
                    >
                      {CONFIDENTIALITY_LABELS[document.confidentiality]}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {uploading && (
        <UploadDialog folderId={folderId || undefined} onClose={() => setUploading(false)} />
      )}
      {newFolder && (
        <FolderDialog parentId={folderId || undefined} onClose={() => setNewFolder(false)} />
      )}
    </div>
  );
}

/** Tells people where their files actually are, and warns if nowhere. */
function StorageBanner() {
  const { data } = useStorageReport();
  if (!data) return null;

  if (data.driver === 'NONE') {
    return (
      <div className="mb-5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <span className="font-medium text-destructive">No file storage is configured.</span> Set
        S3 credentials, or a local storage path, before uploading anything.
      </div>
    );
  }

  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border bg-muted/30 px-4 py-2.5 text-sm text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <HardDrive className="h-4 w-4" />
        {data.totalLabel} across {data.documentCount} documents
      </span>
      <span>{data.versionCount} versions kept</span>
      <span className="ml-auto text-xs">
        {data.driver === 'S3'
          ? 'Stored on S3'
          : 'Stored on this server’s disk — set S3 credentials to move it'}
      </span>
    </div>
  );
}

function FolderTree({
  folders,
  activeId,
  onSelect,
}: {
  folders: Folder[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  const roots = folders.filter((folder) => !folder.parentId);

  const render = (folder: Folder, depth: number): React.ReactNode => {
    const children = folders.filter((candidate) => candidate.parentId === folder.id);
    return (
      <div key={folder.id}>
        <button
          onClick={() => onSelect(folder.id)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
            activeId === folder.id && 'bg-secondary font-medium',
          )}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
        >
          <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          {folder.documentCount > 0 && (
            <span className="tabular shrink-0 text-xs text-muted-foreground">
              {folder.documentCount}
            </span>
          )}
        </button>
        {children.map((child) => render(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-0.5">
      <button
        onClick={() => onSelect('')}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
          !activeId && 'bg-secondary font-medium',
        )}
      >
        <FileText className="h-4 w-4 text-muted-foreground" />
        All documents
      </button>
      {roots.map((folder) => render(folder, 0))}
    </div>
  );
}

function UploadDialog({ folderId, onClose }: { folderId?: string; onClose: () => void }) {
  const upload = useUploadDocument();
  const { data: folders = [] } = useFolders();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    name: '',
    category: 'OTHER',
    confidentiality: 'INTERNAL',
    folderId: folderId ?? '',
    tags: '',
    expiryDate: '',
  });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload a document</DialogTitle>
          <DialogDescription>
            Files are stored outside anything the web server serves, and reached only through a
            link that expires after five minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors hover:border-primary/50 hover:bg-muted/40"
            onClick={() => fileRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const dropped = event.dataTransfer.files[0];
              if (dropped) {
                setFile(dropped);
                if (!form.name) setForm({ ...form, name: dropped.name });
              }
            }}
          >
            <Upload className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            {file ? (
              <div className="text-sm">
                <div className="font-medium">{file.name}</div>
                <div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Drop a file here, or click to choose one
              </p>
            )}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(event) => {
                const chosen = event.target.files?.[0];
                if (chosen) {
                  setFile(chosen);
                  if (!form.name) setForm({ ...form, name: chosen.name });
                }
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Who can see it</Label>
              <Select
                value={form.confidentiality}
                onChange={(e) => setForm({ ...form, confidentiality: e.target.value })}
              >
                {Object.entries(CONFIDENTIALITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Folder</Label>
              <Select
                value={form.folderId}
                onChange={(e) => setForm({ ...form, folderId: e.target.value })}
              >
                <option value="">Unfiled</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.path}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Input
                type="date"
                value={form.expiryDate}
                onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Reminders at 60, 30 and 7 days before.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tags</Label>
            <Input
              value={form.tags}
              placeholder="Comma separated"
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!file || upload.isPending}
            onClick={async () => {
              if (!file) return;
              setError(null);
              try {
                await upload.mutateAsync({
                  file,
                  meta: {
                    name: form.name || undefined,
                    category: form.category,
                    confidentiality: form.confidentiality,
                    folderId: form.folderId || undefined,
                    tags: form.tags || undefined,
                    expiryDate: form.expiryDate || undefined,
                  },
                });
                onClose();
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : 'Could not upload that file.');
              }
            }}
          >
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FolderDialog({ parentId, onClose }: { parentId?: string; onClose: () => void }) {
  const save = useSaveFolder();
  const { data: folders = [] } = useFolders();
  const [name, setName] = useState('');
  const [parent, setParent] = useState(parentId ?? '');
  const [maxFileSizeMb, setMax] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>
            Permissions set on a folder are inherited by everything inside it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Inside</Label>
            <Select value={parent} onChange={(event) => setParent(event.target.value)}>
              <option value="">Top level</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.path}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Largest file allowed (MB)</Label>
            <Input
              type="number"
              min={1}
              max={500}
              placeholder="50"
              value={maxFileSizeMb}
              onChange={(event) => setMax(event.target.value ? Number(event.target.value) : '')}
            />
            <p className="text-xs text-muted-foreground">
              Left blank, the default of 50 MB applies. Raise it for design files and builds.
            </p>
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || name.trim().length < 1}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  name,
                  parentId: parent || undefined,
                  maxFileSizeMb: maxFileSizeMb || undefined,
                });
                onClose();
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : 'Could not create the folder.');
              }
            }}
          >
            Create folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
